import type { RunSettings } from '../multiplayer/RunSettings';
import Phaser from 'phaser';
import { GAME_W, GAME_H } from '../constants';
import { audioSystem } from '../systems/AudioSystem';

// ── Types ────────────────────────────────────────────────────────────────────

interface PauseData extends RunSettings {
  level?: number;
  mode?: string;
  score?: number;
}

interface MenuOption {
  label: string;
  color: number;
  action: () => void;
}

// ── Scene ────────────────────────────────────────────────────────────────────

export default class PauseScene extends Phaser.Scene {
  private selectedIndex = 0;
  private optionTexts: Phaser.GameObjects.Container[] = [];
  private menuOptions: MenuOption[] = [];
  private escKey!: Phaser.Input.Keyboard.Key;
  private upKey!: Phaser.Input.Keyboard.Key;
  private downKey!: Phaser.Input.Keyboard.Key;
  private enterKey!: Phaser.Input.Keyboard.Key;
  private wKey!: Phaser.Input.Keyboard.Key;
  private sKey!: Phaser.Input.Keyboard.Key;
  private initData: PauseData = {};

  constructor() {
    super({ key: 'PauseScene' });
  }

  init(data: PauseData): void {
    this.initData = data ?? {};
    this.selectedIndex = 0;
  }

  create(): void {
    // Ensure this overlay is rendered on top
    this.scene.bringToTop();

    this.buildOverlay();
    this.buildPanel();
    this.buildTitle();
    this.buildOptions();
    this.buildHints();
    this.setupInput();

    // Animate in — scale from center
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 180,
      ease: 'Linear',
    });
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  private buildOverlay(): void {
    // Semi-transparent dark backdrop
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0.62)
      .setDepth(0);

    // Subtle scanline effect
    const lines = this.add.graphics().setDepth(1).setAlpha(0.06);
    lines.fillStyle(0x000000, 1);
    for (let y = 0; y < GAME_H; y += 4) {
      lines.fillRect(0, y, GAME_W, 2);
    }

    // Vignette (fake with dark corner rects)
    const vig = this.add.graphics().setDepth(1).setAlpha(0.3);
    vig.fillStyle(0x000000, 1);
    vig.fillRect(0, 0, GAME_W, 60);
    vig.fillRect(0, GAME_H - 60, GAME_W, 60);
    vig.fillRect(0, 0, 60, GAME_H);
    vig.fillRect(GAME_W - 60, 0, 60, GAME_H);
  }

  private buildPanel(): void {
    const panelW = 360;
    const panelH = 320;
    const cx = GAME_W / 2;
    const cy = GAME_H / 2;

    const panel = this.add.graphics().setDepth(2);

    // Outer glow
    panel.fillStyle(0x001144, 0.5);
    panel.fillRoundedRect(cx - panelW / 2 - 8, cy - panelH / 2 - 8, panelW + 16, panelH + 16, 20);

    // Main panel
    panel.fillStyle(0x070718, 0.97);
    panel.fillRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 14);

    // Border
    panel.lineStyle(2, 0x00ffff, 0.6);
    panel.strokeRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 14);

    // Top accent line
    panel.lineStyle(3, 0x00ffff, 0.9);
    panel.lineBetween(cx - 80, cy - panelH / 2 + 1, cx + 80, cy - panelH / 2 + 1);
  }

  private buildTitle(): void {
    const cx = GAME_W / 2;
    const cy = GAME_H / 2;

    // Shadow
    this.add.text(cx + 2, cy - 130 + 2, 'PAUZĂ', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '52px',
      color: '#000033',
    }).setOrigin(0.5).setDepth(3);

    // Main title
    const title = this.add.text(cx, cy - 130, 'PAUZĂ', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '52px',
      color: '#00ffff',
      stroke: '#003366',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 0, color: '#00ccff', blur: 16, fill: true },
    }).setOrigin(0.5).setDepth(4);

    // Slow pulse
    this.tweens.add({
      targets: title,
      alpha: 0.8,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Level info (if available)
    if (this.initData.level !== undefined) {
      this.add.text(cx, cy - 82, `Nivel ${this.initData.level}`, {
        fontFamily: 'Arial',
        fontSize: '14px',
        color: '#446688',
      }).setOrigin(0.5).setDepth(4);
    }
  }

  private buildOptions(): void {
    const options: MenuOption[] = [
      {
        label: '▶  CONTINUĂ',
        color: 0x00ff88,
        action: () => this.resumeGame(),
      },
      {
        label: '↺  RESTART NIVEL',
        color: 0xffaa00,
        action: () => this.restartLevel(),
      },
      {
        label: '⌂  MENIU PRINCIPAL',
        color: 0xff4444,
        action: () => this.goToMainMenu(),
      },
    ];

    const cx = GAME_W / 2;
    const cy = GAME_H / 2;
    const startY = cy - 30;
    const spacing = 68;
    const btnW = 280;
    const btnH = 50;

    this.optionTexts = [];

    options.forEach((opt, i) => {
      const y = startY + i * spacing;
      const container = this.add.container(cx, y).setDepth(5);

      // Background
      const bg = this.add.graphics();
      bg.fillStyle(0x0d0d2a, 0.9);
      bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
      bg.lineStyle(2, opt.color, 0.5);
      bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
      container.add(bg);

      const label = this.add.text(0, 1, opt.label, {
        fontFamily: 'Arial Black, Arial',
        fontSize: '19px',
        color: Phaser.Display.Color.IntegerToColor(opt.color).rgba,
        stroke: '#000000',
        strokeThickness: 3,
      }).setOrigin(0.5);
      container.add(label);

      // Hit area
      const hit = this.add.rectangle(0, 0, btnW, btnH, 0xffffff, 0);
      hit.setInteractive({ useHandCursor: true });
      container.add(hit);

      // Hover fill
      const glow = this.add.graphics();
      glow.fillStyle(opt.color, 0.1);
      glow.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
      glow.setAlpha(0);
      container.add(glow);

      hit.on('pointerover', () => {
        audioSystem.play('menu_hover');
        this.selectedIndex = i;
        this.refreshSelection();
        glow.setAlpha(1);
        this.tweens.add({ targets: container, scaleX: 1.05, scaleY: 1.05, duration: 80, ease: 'Linear' });
      });

      hit.on('pointerout', () => {
        glow.setAlpha(0);
        this.tweens.add({ targets: container, scaleX: 1, scaleY: 1, duration: 80, ease: 'Linear' });
        this.refreshSelection();
      });

      hit.on('pointerdown', () => {
        audioSystem.play('menu_click');
        this.tweens.add({
          targets: container,
          scaleX: 0.94, scaleY: 0.94,
          duration: 60, yoyo: true, ease: 'Linear',
          onComplete: () => opt.action(),
        });
      });

      // Stagger entrance
      container.setAlpha(0).setX(cx + 300);
      this.tweens.add({
        targets: container,
        x: cx,
        alpha: 1,
        duration: 250,
        delay: 60 + i * 60,
        ease: 'Back.easeOut',
      });

      this.optionTexts.push(container);
    });

    // Store for keyboard nav
    this.menuOptions = options;

    // Highlight first item
    this.time.delayedCall(260, () => this.refreshSelection());
  }

  private buildHints(): void {
    const cy = GAME_H / 2;
    this.add.text(GAME_W / 2, cy + 140, 'ESC — Continuă jocul', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#334455',
    }).setOrigin(0.5).setDepth(4);
  }

  // ── Keyboard Selection ────────────────────────────────────────────────────

  private setupInput(): void {
    const kb = this.input.keyboard!;
    this.escKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    this.upKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    this.downKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
    this.wKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.sKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.S);
    this.enterKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);

    kb.on('keydown-ESC', () => this.resumeGame());

    kb.on('keydown-UP', () => this.moveSelection(-1));
    kb.on('keydown-DOWN', () => this.moveSelection(1));
    kb.on('keydown-W', () => this.moveSelection(-1));
    kb.on('keydown-S', () => this.moveSelection(1));
    kb.on('keydown-ENTER', () => this.activateSelected());
    kb.on('keydown-SPACE', () => this.activateSelected());
  }

  private moveSelection(dir: -1 | 1): void {
    if (this.menuOptions.length === 0) return;
    this.selectedIndex = (this.selectedIndex + dir + this.menuOptions.length) % this.menuOptions.length;
    this.refreshSelection();
    audioSystem.play('menu_hover');
  }

  private refreshSelection(): void {
    if (this.menuOptions.length === 0) return;

    this.optionTexts.forEach((container, i) => {
      const bg = container.getAt(0) as Phaser.GameObjects.Graphics;
      const isSelected = i === this.selectedIndex;
      const opt = this.menuOptions[i];
      if (!opt) return;
      bg.clear();

      if (isSelected) {
        bg.fillStyle(0x111133, 1);
        bg.fillRoundedRect(-140, -25, 280, 50, 10);
        bg.lineStyle(2, opt.color, 1);
        bg.strokeRoundedRect(-140, -25, 280, 50, 10);
        // Selection marker
        bg.fillStyle(opt.color, 1);
        bg.fillTriangle(-130, 0, -118, -7, -118, 7);
      } else {
        bg.fillStyle(0x0d0d2a, 0.9);
        bg.fillRoundedRect(-140, -25, 280, 50, 10);
        bg.lineStyle(2, opt.color, 0.45);
        bg.strokeRoundedRect(-140, -25, 280, 50, 10);
      }
    });
  }

  private activateSelected(): void {
    const container = this.optionTexts[this.selectedIndex];
    const opt = this.menuOptions[this.selectedIndex];
    if (!container || !opt) return;
    audioSystem.play('menu_click');
    this.tweens.add({
      targets: container,
      scaleX: 0.94, scaleY: 0.94,
      duration: 60, yoyo: true, ease: 'Linear',
      onComplete: () => opt.action(),
    });
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  private resumeGame(): void {
    this.cameras.main.fadeOut(150, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      // Stop pause overlay; GameScene was running underneath
      this.scene.stop();
      // Resume GameScene if it was paused
      if (this.scene.isPaused('GameScene')) {
        this.scene.resume('GameScene');
      }
    });
  }

  private restartLevel(): void {
    this.cameras.main.fadeOut(200, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.stop();
      this.scene.stop('GameScene');
      this.scene.start('GameScene', {
        playerCount: this.initData.playerCount, playerNames: this.initData.playerNames, testStage: this.initData.testStage,
        level: this.initData.level ?? 1,
        mode: this.initData.mode ?? 'campaign',
        score: 0,
      });
    });
  }

  private goToMainMenu(): void {
    audioSystem.stopMusic();
    this.cameras.main.fadeOut(250, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.stop();
      this.scene.stop('GameScene');
      this.scene.start('MainMenuScene');
    });
  }
}
